import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { NavegacionServicio } from '@nucleo/navegacion/navegacion-servicio';
import { SesionServicio } from '@nucleo/sesion/sesion-servicio';
import { OpcionMenuDto } from '@nucleo/navegacion/opcion-menu-dto';
import { menuNavegacionFront } from '@app/rutas/menu-navegacion.const';
import { idiomaActual } from '@nucleo/i18n/idioma-actual';
import { traducciones } from '@nucleo/i18n/traduccion-util';
import { TEXTOS_COMPARTIDO } from '@compartido/i18n/compartido-textos';

/** Ancho del panel del menú móvil: el paso 5 de la escala, acotado al ancho de la pantalla. */
const ESTILO_DRAWER_MENU_MOVIL = { width: 'min(100vw, var(--eq-drawer-5))' };

/** Sangría (px) que se suma por cada nivel de profundidad del árbol del menú. */
const SANGRIA_POR_NIVEL_PX = 20;

@Component({
  selector: 'app-menu-lateral',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgTemplateOutlet,
    ButtonModule,
    DrawerModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './menu-lateral.html',
  styleUrl: './menu-lateral.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuLateral {
  private readonly servicioNavegacion = inject(NavegacionServicio);
  private readonly servicioSesion = inject(SesionServicio);
  private readonly router = inject(Router);

  protected readonly menuMovilAbierto = this.servicioNavegacion.menuMovilAbierto;
  protected readonly sangriaPorNivel = SANGRIA_POR_NIVEL_PX;
  protected readonly estiloDrawer = ESTILO_DRAWER_MENU_MOVIL;

  protected readonly t = traducciones(TEXTOS_COMPARTIDO);
  protected readonly cargandoMenu = this.servicioNavegacion.cargandoMenu;
  protected readonly errorMenu = this.servicioNavegacion.errorMenu;

  /** Primero el menú del backend (ya filtrado por permisos) y luego las opciones propias del front por rol. */
  protected readonly secciones = computed(() => [
    ...this.servicioNavegacion.seccionesDelBackend(),
    ...this.servicioNavegacion.filtrarPorRol(
      menuNavegacionFront(idiomaActual()),
      this.servicioSesion.usuario()?.roles ?? [],
    ),
  ]);

  /** Grupos colapsados por el usuario (por defecto todos se muestran expandidos). */
  private readonly gruposColapsados = signal<ReadonlySet<string>>(new Set());

  protected estaExpandido(identificador: string): boolean {
    return !this.gruposColapsados().has(identificador);
  }

  protected alternarGrupo(identificador: string): void {
    this.gruposColapsados.update((actual) => {
      const nuevo = new Set(actual);
      if (!nuevo.delete(identificador)) nuevo.add(identificador);
      return nuevo;
    });
  }

  protected tieneHijoActivo(opcion: OpcionMenuDto): boolean {
    const urlActual = this.router.url.split('?')[0];
    const buscar = (opciones: OpcionMenuDto[]): boolean =>
      opciones.some(
        (hija) =>
          (!!hija.ruta && (urlActual === hija.ruta || urlActual.startsWith(`${hija.ruta}/`))) ||
          (!!hija.hijos && buscar(hija.hijos)),
      );
    return !!opcion.hijos && buscar(opcion.hijos);
  }

  protected alCambiarVisibilidadDrawer(visible: boolean): void {
    if (visible) {
      this.servicioNavegacion.abrirMenuMovil();
    } else {
      this.servicioNavegacion.cerrarMenuMovil();
    }
  }

  protected reintentarCargaMenu(): void {
    this.servicioNavegacion.cargarMenu();
  }

  protected cerrarMenuMovil(): void {
    this.servicioNavegacion.cerrarMenuMovil();
  }
}
